/**
 * Phase — Google Maps SDK loader (singleton, session-scoped).
 *
 * Architecture:
 *   - `loadMaps()` fetches the API key from GET /api/config/maps-key
 *     (authenticated, no-store) exactly once per session and injects
 *     the Google Maps JS SDK <script> tag exactly once.
 *   - Repeated calls return the same in-flight promise so consumer
 *     components never race.
 *   - Failures (503 no-key, network) reject with a stable error so
 *     surfaces can render their empty state without spinning forever.
 *   - No Geocoding, Roads, or Weather libraries are requested.
 *
 * Consumers should import `useGoogleMaps()` and render Map primitives
 * from `./primitives.jsx`. They must NOT touch `window.google` directly.
 */
import { api } from "@/lib/api";

const SCRIPT_ID = "aa-google-maps-sdk";
let inflight = null;

export function loadMaps() {
  if (inflight) return inflight;
  inflight = (async () => {
    if (window.google && window.google.maps) return window.google.maps;
    // Fetch key from backend at boot; endpoint sets Cache-Control: no-store.
    let key;
    try {
      const r = await api.get("/config/maps-key");
      key = r?.data?.key;
    } catch (e) {
      inflight = null; // allow retry on next surface mount
      throw new Error("maps_key_unavailable");
    }
    if (!key) throw new Error("maps_key_unavailable");
    return new Promise((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) {
        existing.addEventListener("load", () => resolve(window.google.maps));
        existing.addEventListener("error", () => reject(new Error("maps_sdk_load_failed")));
        return;
      }
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.async = true;
      s.defer = true;
      // No `libraries=…` param → do NOT pull Geocoding / Places / Roads.
      s.src = `https://maps.googleapis.com/maps/api/js?v=quarterly&key=${encodeURIComponent(key)}`;
      s.onload = () => resolve(window.google.maps);
      s.onerror = () => {
        inflight = null;
        reject(new Error("maps_sdk_load_failed"));
      };
      document.head.appendChild(s);
    });
  })();
  return inflight;
}
